import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import fileDownload from 'js-file-download'
import { EventsProxy } from 'services'
import { loadBibNumbers, resetValue } from 'state/modules/events'
import { CONTENT_TYPES, FORM_STATES, STRING_EMPTY } from 'utils/constants'
import { showSnackbarError } from 'utils/snackbar'

import { DistanceBlock } from '../DistanceBlock'

import { useStyles } from './BibNumberForm.style'

const BibNumberForm = ({ distances = [], items = {}, mode = FORM_STATES.CREATE }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const inputRef = useRef()

  const { createBibNumbersError, updateBibNumbersError, deleteBibNumbersError, event } =
    useSelector((state) => state.events)

  const [loadingState, setLoadingState] = useState({
    importProgress: false,
    exportProgress: false
  })

  useEffect(() => {
    if (createBibNumbersError) showSnackbarError(createBibNumbersError)

    return () => dispatch(resetValue('createBibNumbersError'))
  }, [createBibNumbersError])

  useEffect(() => {
    if (deleteBibNumbersError) showSnackbarError(deleteBibNumbersError)

    return () => dispatch(resetValue('deleteBibNumbersError'))
  }, [deleteBibNumbersError])

  useEffect(() => {
    if (updateBibNumbersError) showSnackbarError(updateBibNumbersError)

    return () => dispatch(resetValue('updateBibNumbersError'))
  }, [updateBibNumbersError])

  const downloadBibNumbers = async () => {
    setLoadingState((prevState) => ({ ...prevState, exportProgress: true }))

    try {
      const proxy = new EventsProxy()

      const data = await proxy.downloadBibNumbers(event.id)
      fileDownload(data, `BibNumbers_Event_${event.name}.xlsx`)
    } catch (error) {
      showSnackbarError(error)
    } finally {
      setLoadingState((prevState) => ({ ...prevState, exportProgress: false }))
    }
  }

  const importBibNumbers = async (e) => {
    setLoadingState((prevState) => ({ ...prevState, importProgress: true }))

    if (e.target.files.length === 0) return

    try {
      const file = e.target.files[0]
      const validResultFormats = [CONTENT_TYPES.APPLICATION_EXCEL]

      e.target.value = STRING_EMPTY

      if (!validResultFormats.includes(file.type))
        throw new Error('Los números deben ser importados en formato Excel')

      const proxy = new EventsProxy()
      const data = await proxy.importBibNumbers(event.id, file)

      dispatch(loadBibNumbers(event.id))

      return data
    } catch (error) {
      showSnackbarError(error)
    } finally {
      setLoadingState((prevState) => ({ ...prevState, importProgress: false }))
    }
  }

  return (
    <div className={classes.form}>
      <Typography color='primary' variant='h6' className={classes.title}>
        {mode === FORM_STATES.CREATE
          ? 'Seleccione la distancia para la cual creará números.'
          : 'Seleccione la distancia para acceder a la edición de números.'}
      </Typography>

      <div className={classes.distanceContainer}>
        {distances.map((x, index) => (
          <DistanceBlock
            name={x.label}
            id={x.value}
            mode={mode}
            key={index}
            categoryList={items[x.value].map((c) => ({
              value: c.eventCategoryId,
              label: c.eventCategoryName
            }))}
          />
        ))}
      </div>
      <div className={classes.buttonContainer}>
        <Button
          color='secondary'
          className={classes.button}
          variant='contained'
          onClick={() => inputRef.current.click()}
          endIcon={loadingState.importProgress && <CircularProgress size={16} color='primary' />}
          disabled={loadingState.exportProgress || loadingState.importProgress}>
          Importar
        </Button>

        <input
          type='file'
          name='uploadResults'
          ref={inputRef}
          onChange={importBibNumbers}
          accept='.xlsx'
          style={{ display: 'none' }}
        />
        <Button
          color='secondary'
          className={classes.button}
          variant='contained'
          endIcon={loadingState.exportProgress && <CircularProgress size={16} color='primary' />}
          disabled={loadingState.exportProgress || loadingState.importProgress}
          onClick={downloadBibNumbers}>
          Exportar
        </Button>
      </div>
    </div>
  )
}

export default BibNumberForm
